import {Box, HBox} from '../../components/core/view';
import {ClaimStatus, OutreachShift, OutreachStatus} from '../types/shiftClaim';
import {CustomText, H4Text, H5Text, H6Text} from '../../components/core/text';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {ScrollView, StyleSheet, View} from 'react-native';
import {
  useClaimShift,
  useFetchAvailableShifts,
  useRejectShift,
  useShiftClaimContext,
  useShiftClaimReducer,
  useShiftClaimUrlParams,
} from '../hooks/shiftClaim';

import Header from '../../components/composites/Header/Header';
import If from '@buncha/components/conditional/If';
import ShiftClaimActionSheet from '../components/ShiftClaimActionSheet';
import ShiftClaimCard from '../components/ShiftClaimCard';
import {ShiftClaimContext} from '../context/shiftClaim';
import ShiftClaimError from '../components/ShiftClaimError';
import ShiftClaimSkeleton from '../components/ShiftClaimSkeleton';
import font from '@buncha/appStyles/style/font';
import {useColorThemeType} from '../../appStyles/hooks/theme';

const ShiftClaimScreen: React.FC = () => {
  const [state, dispatch] = useShiftClaimReducer();
  const provider = useMemo(() => ({state, dispatch}), [state, dispatch]);

  return (
    <ShiftClaimContext.Provider value={provider}>
      <ShiftClaimContent />
    </ShiftClaimContext.Provider>
  );
};

const ShiftClaimContent: React.FC = () => {
  const theme = useColorThemeType('main');
  const {wuserId} = useShiftClaimUrlParams();
  const {state} = useShiftClaimContext();
  const {fetchShifts} = useFetchAvailableShifts();
  const {claimShift} = useClaimShift();
  const {rejectShift} = useRejectShift();

  const [actionState, setActionState] = useState<{
    isOpen: boolean;
    actionType: 'release' | 'pass' | null;
    shift: OutreachShift | null;
  }>({isOpen: false, actionType: null, shift: null});

  useEffect(() => {
    fetchShifts(wuserId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {claimedShifts, availableShifts} = useMemo(() => {
    const claimed: OutreachShift[] = [];
    const available: OutreachShift[] = [];
    for (const s of state.shifts) {
      const isClaimed =
        s.status === OutreachStatus.Accepted || state.claimedIds.includes(s.id);
      const isRejected =
        s.status === OutreachStatus.Rejected ||
        state.rejectedIds.includes(s.id);
      if (isRejected) continue;
      if (isClaimed) claimed.push(s);
      else available.push(s);
    }
    return {claimedShifts: claimed, availableShifts: available};
  }, [state.shifts, state.claimedIds, state.rejectedIds]);

  const openActionSheet = useCallback(
    (action: 'release' | 'pass', shift: OutreachShift) =>
      setActionState({isOpen: true, actionType: action, shift}),
    [],
  );

  const closeActionSheet = useCallback(
    () => setActionState(prev => ({...prev, isOpen: false})),
    [],
  );

  const handleClaim = useCallback(
    (shiftId: string) => {
      claimShift(shiftId);
    },
    [claimShift],
  );

  const handlePass = useCallback(
    (shift: OutreachShift) => openActionSheet('pass', shift),
    [openActionSheet],
  );

  const handleRelease = useCallback(
    (shift: OutreachShift) => openActionSheet('release', shift),
    [openActionSheet],
  );

  const handleConfirmAction = useCallback(async () => {
    const shiftId = actionState.shift?.id;
    if (!shiftId) return;
    closeActionSheet();
    await rejectShift(
      shiftId,
      actionState.actionType === 'release' ? 'cancelled' : 'rejected',
    );
  }, [
    actionState.shift,
    actionState.actionType,
    rejectShift,
    closeActionSheet,
  ]);

  if (!wuserId) return <ShiftClaimError status={ClaimStatus.Invalid} />;

  if (state.fetchStatus === ClaimStatus.Loading) return <ShiftClaimSkeleton />;

  if (
    state.fetchStatus === ClaimStatus.NetworkError ||
    state.fetchStatus === ClaimStatus.Invalid
  )
    return <ShiftClaimError status={state.fetchStatus} />;

  return (
    <Header
      headerColor={theme?.background?.blue?.[300]}
      hideUploadFile
      hideBottomSafeArea
      headerRightComponent={
        <HBox
          style={[
            styles.headerRight,
            {backgroundColor: theme?.background?.blue?.[300]},
          ]}>
          <H4Text
            style={[styles.sectionLabel, {color: theme?.text.white[100]}]}>
            Your Shifts
          </H4Text>
        </HBox>
      }>
      <ScrollView
        contentContainerStyle={[
          styles.scrollContent,
          {backgroundColor: theme?.background.gray[200]},
        ]}
        showsVerticalScrollIndicator={false}>
        <View style={styles.inner}>
          {claimedShifts.length > 0 && (
            <Box style={styles.sectionContainer}>
              <HBox style={styles.sectionHeaderRow}>
                <CustomText
                  style={[
                    styles.sectionTitle,
                    {color: theme?.text.green[100]},
                  ]}>
                  YOUR CLAIMED SHIFTS
                </CustomText>
                <CustomText
                  style={[
                    styles.sectionCount,
                    {color: theme?.text.green[100]},
                  ]}>
                  {claimedShifts.length}
                </CustomText>
                <View
                  style={[
                    styles.divider,
                    {backgroundColor: theme?.background.gray[100]},
                  ]}
                />
              </HBox>

              {claimedShifts.map(shift => (
                <ShiftClaimCard
                  key={shift.id}
                  shift={shift}
                  isClaimed={true}
                  isLoading={state.claimingId === shift.id}
                  onReleasePress={handleRelease}
                />
              ))}
            </Box>
          )}

          <If condition={availableShifts?.length > 0}>
            <Box style={styles.sectionContainer}>
              <HBox style={styles.sectionHeaderRow}>
                <CustomText
                  style={[styles.sectionTitle, {color: theme?.text.blue[300]}]}>
                  {claimedShifts.length > 0
                    ? 'MORE AVAILABLE'
                    : 'PICK UP A SHIFT'}
                </CustomText>
                <H6Text
                  style={[styles.sectionCount, {color: theme?.text.gray[100]}]}>
                  {availableShifts.length}
                </H6Text>
                <View
                  style={[
                    styles.divider,
                    {backgroundColor: theme?.text.gray[100]},
                  ]}
                />
              </HBox>
              {availableShifts.map(shift => (
                <ShiftClaimCard
                  key={shift.id}
                  shift={shift}
                  isClaimed={false}
                  isLoading={state.claimingId === shift.id}
                  onClaim={handleClaim}
                  onPassPress={handlePass}
                />
              ))}
            </Box>
          </If>
        </View>
        <If
          condition={
            state.shifts?.length === 0 ||
            (claimedShifts.length === 0 && availableShifts.length === 0)
          }>
          <Box style={styles.emptyContainer}>
            <H5Text style={[styles.emptyText, {color: theme?.text.gray[100]}]}>
              No shifts available at this time.
            </H5Text>
          </Box>
        </If>
      </ScrollView>

      <ShiftClaimActionSheet
        isOpen={actionState.isOpen}
        onClose={closeActionSheet}
        actionType={actionState.actionType}
        shift={actionState.shift}
        isLoading={state.rejectingId === actionState.shift?.id}
        onConfirm={handleConfirmAction}
      />
    </Header>
  );
};

const styles = StyleSheet.create({
  headerRight: {
    flex: 1,
    justifyContent: 'center',
    paddingVertical: 12,
  },
  scrollContent: {
    flexGrow: 1,
  },
  inner: {
    padding: 20,
    maxWidth: 520,
    alignSelf: 'center',
    width: '100%',
  },
  sectionLabel: {
    letterSpacing: 0.8,
    marginBottom: 4,
  },
  emptyContainer: {
    paddingVertical: 48,
    alignItems: 'center',
  },
  emptyText: {
    textAlign: 'center',
  },
  sectionContainer: {
    marginBottom: 12,
  },
  sectionHeaderRow: {
    marginBottom: 12,
    alignItems: 'center',
  },
  sectionTitle: {
    letterSpacing: 1.2,
    fontFamily: font.fontTheme.muli[900].normal,
  },
  sectionCount: {
    marginLeft: 8,
    marginRight: 8,
    fontFamily: font.fontTheme.muli[900].normal,
  },
  divider: {
    flex: 1,
    height: 1,
  },
});

export default ShiftClaimScreen;
